/**
 * Amos ID OIDC Authentication Utilities
 * Handles PKCE flow for Amos ID integration
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Generate cryptographically secure random string
 */
function generateRandomString(length = 32) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate PKCE code verifier (43-128 characters, URL-safe)
 */
export function generateCodeVerifier() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
}

/**
 * Generate PKCE code challenge from verifier using SHA-256
 */
export async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64URLEncode(new Uint8Array(digest));
}

/**
 * Base64 URL encode (no padding, URL-safe chars)
 */
function base64URLEncode(buffer) {
  const base64 = btoa(String.fromCharCode(...buffer));
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Generate state parameter for CSRF protection
 */
export function generateState() {
  return generateRandomString(24);
}

/**
 * Initiate Amos ID login flow
 * Stores PKCE params in sessionStorage and redirects to Amos ID
 * 
 * @param {Object} options
 * @param {string} options.returnUrl - Where to redirect after successful auth
 */
export async function initiateAmosIdLogin(options = {}) {
  const { returnUrl = '/' } = options;

  try {
    // Fetch Amos ID config from backend
    const configResponse = await fetch(`${API_URL}/api/auth/amos-id/config`);
    if (!configResponse.ok) {
      throw new Error('No se pudo obtener configuración de Amos ID');
    }
    const config = await configResponse.json();

    if (!config.enabled) {
      throw new Error('Amos ID no está habilitado');
    }

    // Generate PKCE pair
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateState();

    // Store in sessionStorage for callback verification
    sessionStorage.setItem('amos_id_code_verifier', codeVerifier);
    sessionStorage.setItem('amos_id_state', state);
    sessionStorage.setItem('amos_id_return_url', returnUrl);

    // Build authorization URL
    const redirectUri = `${window.location.origin}/auth/callback`;
    const scopes = (config.scopes || ['openid', 'profile', 'email', 'roles']).join(' ');
    
    // Use discovery endpoint or fallback
    const authEndpoint = config.authorization_endpoint || 
      `${config.issuer}/amos-id/v1/authorize`;

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: config.client_id,
      redirect_uri: redirectUri,
      scope: scopes,
      state: state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    const authUrl = `${authEndpoint}?${params.toString()}`;
    
    // Redirect to Amos ID
    window.location.href = authUrl;
    
  } catch (error) {
    console.error('Amos ID login initiation failed:', error);
    throw error;
  }
}

/**
 * Check if Amos ID is configured and available
 */
export async function isAmosIdAvailable() {
  try {
    const response = await fetch(`${API_URL}/api/auth/amos-id/config`);
    if (!response.ok) return false;
    const config = await response.json();
    return config.enabled === true;
  } catch {
    return false;
  }
}

/**
 * Logout from Amos ID (if supported)
 * Clears local session data
 */
export function clearAmosIdSession() {
  sessionStorage.removeItem('amos_id_code_verifier');
  sessionStorage.removeItem('amos_id_state');
  sessionStorage.removeItem('amos_id_return_url');
}

export default {
  initiateAmosIdLogin,
  isAmosIdAvailable,
  clearAmosIdSession,
  generateCodeVerifier,
  generateCodeChallenge,
  generateState,
};
